import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Header } from '@/components/landing/Header';
import { Footer } from '@/components/landing/Footer';

/**
 * Shared shell for MANU VET static legal pages (mentions légales, RGPD).
 * Keeps the header + footer consistent with the landing and centers content.
 */
export const LegalPage = ({ title, kicker, updatedAt, children, testId }) => {
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'instant' });
    }, []);

    return (
        <div className="min-h-screen bg-black text-white">
            <Header />
            <main
                data-testid={testId}
                className="relative pt-28 sm:pt-36 pb-20 sm:pb-28 border-b border-white/5"
            >
                <div className="absolute -top-32 right-10 h-[420px] w-[420px] blur-orb-yellow opacity-40 pointer-events-none" />

                <div className="relative mx-auto max-w-4xl px-5 sm:px-8">
                    <Link
                        to="/"
                        data-testid="legal-back-home"
                        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-white/60 hover:text-[#FFD500] transition-colors"
                    >
                        <ArrowLeft className="h-3.5 w-3.5" /> Retour à l'accueil
                    </Link>

                    <div className="mt-6">
                        <span className="section-label">{kicker}</span>
                        <h1 className="mt-4 font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] text-white">
                            {title}
                            <span className="text-[#FFD500]">.</span>
                        </h1>
                        {updatedAt && (
                            <p className="mt-4 text-[11px] uppercase tracking-[0.22em] text-white/45">
                                Dernière mise à jour · {updatedAt}
                            </p>
                        )}
                    </div>

                    <div className="mt-12 legal-prose">{children}</div>
                </div>
            </main>
            <Footer />

            <style>{`
                .legal-prose { color: rgba(255,255,255,0.78); font-size: 15px; line-height: 1.7; }
                .legal-prose h2 {
                    font-family: 'Anton', sans-serif;
                    text-transform: uppercase;
                    letter-spacing: 0.02em;
                    font-size: 1.6rem;
                    color: #fff;
                    margin: 2.6rem 0 1rem;
                    padding-bottom: .5rem;
                    border-bottom: 1px solid rgba(255,213,0,0.25);
                }
                .legal-prose h3 {
                    font-family: 'Manrope', sans-serif;
                    text-transform: uppercase;
                    letter-spacing: 0.18em;
                    font-size: 0.72rem;
                    color: #FFD500;
                    font-weight: 700;
                    margin: 1.6rem 0 0.6rem;
                }
                .legal-prose p { margin: 0.75rem 0; }
                .legal-prose ul { padding-left: 1.1rem; margin: 0.5rem 0 1rem; }
                .legal-prose li { margin: 0.35rem 0; list-style: none; position: relative; padding-left: 0.9rem; }
                .legal-prose li::before {
                    content: '';
                    position: absolute;
                    left: 0; top: 0.65rem;
                    width: 6px; height: 6px;
                    background: #FFD500;
                }
                .legal-prose a { color: #FFD500; text-decoration: none; border-bottom: 1px solid rgba(255,213,0,0.35); }
                .legal-prose a:hover { border-bottom-color: #FFD500; }
                .legal-prose strong { color: #fff; font-weight: 700; }
                .legal-prose .info-block {
                    margin: 1.2rem 0;
                    padding: 1.1rem 1.2rem;
                    background: #0F0F0F;
                    border: 1px solid rgba(255,255,255,0.08);
                    border-left: 3px solid #FFD500;
                }
                .legal-prose .info-block dt {
                    font-size: 10px;
                    text-transform: uppercase;
                    letter-spacing: 0.22em;
                    color: rgba(255,255,255,0.55);
                    font-weight: 700;
                    margin-top: 0.6rem;
                }
                .legal-prose .info-block dt:first-child { margin-top: 0; }
                .legal-prose .info-block dd { color: #fff; margin: 0.15rem 0 0; font-size: 15px; }
            `}</style>
        </div>
    );
};

export default LegalPage;
